import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { api, formatError, maskCPF, maskPhone } from "../../lib/api";

const EMPTY = { nome_completo: "", cpf: "", telefone: "", email: "", data_nascimento: "", password: "", status: "active" };

function F({ label, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// member=null → create mode; member=object → edit mode
export function MemberFormDialog({ open, member, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!member;

  useEffect(() => {
    if (open) setForm(member ? { ...EMPTY, ...member, cpf: maskCPF(member.cpf || ""), password: "" } : EMPTY);
  }, [open, member]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const { nome_completo, telefone, email, data_nascimento, status } = form;
        await api.patch(`/admin/members/${member.id}`, { nome_completo, telefone, email, data_nascimento, status });
        toast.success("Dados atualizados");
      } else {
        const { data } = await api.post("/admin/members", form);
        toast.success(`Associado criado · ${data.member.cadastro_number}`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="member-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar associado" : "Novo associado"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <F label="Nome completo">
            <Input required minLength={3} value={form.nome_completo} onChange={set("nome_completo")} data-testid="form-nome" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="CPF">
              <Input required disabled={isEdit} value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} data-testid="form-cpf" />
            </F>
            <F label="Telefone">
              <Input required value={form.telefone} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} data-testid="form-telefone" />
            </F>
          </div>
          <F label="E-mail">
            <Input required type="email" value={form.email} onChange={set("email")} data-testid="form-email" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Nascimento">
              <Input required type="date" value={form.data_nascimento} onChange={set("data_nascimento")} data-testid="form-nascimento" />
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="blocked">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          {!isEdit && (
            <F label="Senha inicial do associado">
              <Input required minLength={6} type="text" value={form.password} onChange={set("password")} data-testid="form-password" placeholder="Mínimo 6 caracteres" />
            </F>
          )}
          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#166534] hover:bg-[#14532d] hover:text-white text-white" data-testid="form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
